import { useEffect } from 'react';
import invariant from 'tiny-invariant';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useTableParams } from 'business/providers/table-params';
import { SegmentHistoryTableFilterData } from 'business/quality-control/pages/segment-history';
import { useSegmentHistoryFilters } from 'business/quality-control/pages/segment-history/providers/filters';
import { useSegmentHistoryV1ControllerGetSegmentsHistory } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';
import { SORT_ORDER } from 'technical/graphql/sort-order';

import { formatFilterParams } from './utils';

export const useGetSegmentHistoryData = (
  setFilterData: FilterDataSetter<SegmentHistoryTableFilterData>,
) => {
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');

  const filters = useSegmentHistoryFilters();

  const headers = useRestApiHeaders(Module_Enum.QualityScan);

  const tableParams = useTableParams();

  const sortField = tableParams.tableParams.sortField;
  const sortOrder = tableParams.tableParams.sortOrder
    ? SORT_ORDER[tableParams.tableParams.sortOrder]
    : null;

  const { data, isLoading, error, refetch } =
    useSegmentHistoryV1ControllerGetSegmentsHistory({
      pathParams: {
        constructionSiteId: currentConstructionSiteId,
      },
      queryParams: {
        ...formatFilterParams(filters),
        paginationPage: tableParams.tableParams.current - 1, // we want to start at index 0,
        paginationSize: tableParams.tableParams.pageSize,
        paginationSortField: sortField?.toString(),
        paginationSortDirection: sortOrder,
      },
      headers,
    });

  useEffect(() => {
    setFilterData({
      selectedSegments: data?.total ?? 0,
    });
  }, [data, filters, setFilterData]);

  return {
    data: data?.entries ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error,
    refetch,
  };
};
